import {type FormEvent, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {ChevronDownIcon, ChevronRightIcon} from "lucide-react";
import {toast} from "sonner";

import {authRegister} from "@/sdk";

import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Input} from "@/components/ui/input";

type Mode = "create" | "join";

// The /auth/register endpoint accepts EITHER `org_name` (auto-create a new
// organization, the registering user becomes its owner) OR `org_id` (join an
// existing org as a member). Exactly one must be supplied. The backend
// currently returns the new user only — no token bundle — so on success we
// route to /login with a success toast and let them sign in.
export const Register = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("create");
  const [joinExpanded, setJoinExpanded] = useState(false);

  // Shared
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  // Mode: create
  const [orgName, setOrgName] = useState("");
  // Mode: join
  const [orgId, setOrgId] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body =
        mode === "create"
          ? {
              org_name: orgName.trim(),
              email: email.trim(),
              password,
              display_name: displayName.trim() || undefined,
            }
          : {
              org_id: orgId.trim(),
              email: email.trim(),
              password,
              display_name: displayName.trim() || undefined,
            };

      const {data, error: apiErr} = await authRegister({body});
      if (apiErr || !data) {
        const detail = (apiErr as {detail?: string} | undefined)?.detail ?? "Registration failed";
        setError(detail);
        return;
      }
      toast.success("Account created. Please sign in.");
      navigate("/login", {replace: true});
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const switchToJoin = () => {
    setMode("join");
    setJoinExpanded(true);
    setError(null);
  };

  const switchToCreate = () => {
    setMode("create");
    setError(null);
  };

  return (
    <div className="mx-auto flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg md:text-xl">Create account</CardTitle>
          <CardDescription className="text-xs md:text-sm">
            {mode === "create"
              ? "Spin up a new organization. You'll be its owner."
              : "Join an existing organization with its UUID."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {mode === "create" ? (
              <div className="space-y-2">
                <label htmlFor="org_name" className="text-sm font-medium leading-none">
                  Organization name
                </label>
                <Input
                  id="org_name"
                  placeholder="Acme Hosting"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label htmlFor="org_id" className="text-sm font-medium leading-none">
                  Organization ID
                </label>
                <Input
                  id="org_id"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  required
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="display_name" className="text-sm font-medium leading-none">
                Display name
              </label>
              <Input
                id="display_name"
                placeholder="Jane Doe"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium leading-none">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium leading-none">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Choose a strong password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error ? (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting
                ? "Creating account…"
                : mode === "create"
                  ? "Create organization & account"
                  : "Join organization"}
            </Button>
          </form>

          <div className="space-y-2 border-t pt-4">
            {mode === "create" ? (
              <button
                type="button"
                onClick={() => setJoinExpanded((v) => !v)}
                className="flex w-full items-center gap-1 text-left text-sm text-muted-foreground hover:text-foreground"
              >
                {joinExpanded ? (
                  <ChevronDownIcon className="size-4" />
                ) : (
                  <ChevronRightIcon className="size-4" />
                )}
                Joining an existing organization?
              </button>
            ) : (
              <button
                type="button"
                onClick={switchToCreate}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                ← Back to creating a new organization
              </button>
            )}

            {mode === "create" && joinExpanded ? (
              <p className="text-xs text-muted-foreground">
                If your team already has an Eyrie organization, ask an admin for its UUID
                and{" "}
                <button
                  type="button"
                  onClick={switchToJoin}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  switch to join mode
                </button>
                .
              </p>
            ) : null}
          </div>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary underline-offset-4 hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
